// src/pages/AboutPage.js
import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const AboutContainer = styled.div`
    min-height: 100vh;
    background: #0a0a0a;
    color: #ffffff;
    padding-top: 80px;
`;

// Hero Section
const HeroSection = styled.section`
    padding: 4rem 2rem;
    text-align: center;
    background: radial-gradient(ellipse at center, rgba(99, 102, 241, 0.08) 0%, transparent 70%);
`;

const HeroContent = styled.div`
    width: 100%;
    padding: 0 2rem;
    margin: 0 auto;
    
    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const MainTitle = styled(motion.h1)`
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const Subtitle = styled(motion.h2)`
    font-size: 1.5rem;
    color: #94a3b8;
    font-weight: 400;
    margin-bottom: 2rem;
    line-height: 1.4;
`;

const Introduction = styled(motion.p)`
    font-size: 1.2rem;
    color: #94a3b8;
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto 3rem;
`;

// Main Content Sections
const ContentSection = styled.section`
    padding: 4rem 2rem;
    width: 100%;
`;

const SectionTitle = styled(motion.h2)`
    font-size: clamp(2rem, 4vw, 2.5rem);
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
    color: #94a3b8;
    font-size: 1.1rem;
    margin-bottom: 3rem;
    max-width: 600px;
`;

// Skills & Expertise
const SkillsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 4rem;
`;

const SkillCategory = styled(motion.div)`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        transform: translateY(-4px);
        border-color: rgba(99, 102, 241, 0.3);
    }
`;

const CategoryTitle = styled.h3`
    font-size: 1.3rem;
    font-weight: 600;
    color: #6366f1;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const SkillsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const SkillItem = styled.li`
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;
    
    &:before {
        content: '→';
        position: absolute;
        left: 0;
        color: #6366f1;
        font-weight: bold;
    }
`;

// Stats Section
const StatsSection = styled(motion.section)`
    background: rgba(255, 255, 255, 0.02);
    padding: 4rem 2rem;
    margin: 4rem 0;
`;

const StatsGrid = styled.div`
    max-width: 1000px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 2rem;
    text-align: center;
`;

const StatCard = styled(motion.div)`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem 1rem;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        border-color: rgba(99, 102, 241, 0.3);
    }
`;

const StatNumber = styled.div`
    font-size: 2.5rem;
    font-weight: 700;
    color: #6366f1;
    margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
    color: #94a3b8;
    font-size: 1rem;
`;

// Journey Section
const JourneySection = styled.section`
    padding: 4rem 2rem;
    max-width: 1000px;
    margin: 0 auto;
`;

const JourneyGrid = styled.div`
    display: grid;
    gap: 2rem;
`;

const JourneyItem = styled(motion.div)`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    border-left: 4px solid #6366f1;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        transform: translateX(8px);
    }
`;

const JourneyTitle = styled.h3`
    font-size: 1.3rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.5rem;
`;

const JourneyDescription = styled.p`
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 1rem;
`;

const JourneyTech = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const TechTag = styled.span`
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    font-size: 0.8rem;
    border: 1px solid rgba(99, 102, 241, 0.2);
`;

// Values Section
const ValuesSection = styled.section`
    background: rgba(255, 255, 255, 0.02);
    padding: 4rem 2rem;
    text-align: center;
`;

const ValuesGrid = styled.div`
    max-width: 1000px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
`;

const ValueCard = styled(motion.div)`
    padding: 2rem 1rem;
`;

const ValueIcon = styled.div`
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    font-size: 1.5rem;
`;

const ValueTitle = styled.h3`
    font-size: 1.2rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
    color: #94a3b8;
    line-height: 1.6;
`;

// CTA Section
const CTASection = styled.section`
    padding: 4rem 2rem;
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
`;

const CTAButton = styled(motion.button)`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1.1rem;
    margin: 0 0.5rem;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 40px rgba(99, 102, 241, 0.3);
    }
`;

const SecondaryButton = styled(motion.button)`
    background: transparent;
    color: #6366f1;
    border: 2px solid #6366f1;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1.1rem;
    margin: 0 0.5rem;

    &:hover {
        background: #6366f1;
        color: white;
        transform: translateY(-2px);
    }
`;

const AboutPage = () => {
    const containerRef = useRef(null);
    const navigate = useNavigate();

    const skillsData = [
        {
            icon: "🤖",
            title: "AI & Machine Learning",
            skills: [
                "Large Language Models (GPT, BERT, T5)",
                "Deep Learning (TensorFlow, PyTorch)",
                "Computer Vision (OpenCV, YOLO)",
                "Natural Language Processing",
                "Neural Network Architecture Design"
            ]
        },
        {
            icon: "💻",
            title: "Full-Stack Development",
            skills: [
                "React.js, Next.js, TypeScript",
                "Node.js, Python, Flask, FastAPI",
                "PostgreSQL, MongoDB, Redis",
                "REST APIs, GraphQL, WebSocket",
                "AWS, Docker, Kubernetes"
            ]
        },
        {
            icon: "📊",
            title: "Data Science",
            skills: [
                "Data Analysis (Pandas, NumPy)",
                "Statistical Modeling & Analysis",
                "Big Data Processing (Spark)",
                "Business Intelligence (Tableau)",
                "Predictive Analytics & Forecasting"
            ]
        },
        {
            icon: "🔬",
            title: "Research & Innovation",
            skills: [
                "Neural Network Simulation",
                "Brain Plasticity Modeling",
                "Academic Research & Writing",
                "Innovation Management",
                "Cross-disciplinary Collaboration"
            ]
        }
    ];

    const journeyData = [
        {
            title: "Started My AI Journey",
            description: "Began exploring artificial intelligence and machine learning during my Computer Science studies, fascinated by the potential to create intelligent systems.",
            tech: ["Python", "Machine Learning", "Data Science"]
        },
        {
            title: "Research & Development",
            description: "Conducted groundbreaking research on brain plasticity simulation and neural network modeling at Saveetha Medical College.",
            tech: ["Neural Networks", "Research", "TensorFlow", "PyTorch"]
        },
        {
            title: "Industry Experience",
            description: "Applied AI knowledge in real-world scenarios, developing production-ready solutions and optimizing business processes.",
            tech: ["AI Solutions", "Database Optimization", "Full-Stack Development"]
        },
        {
            title: "AI Consulting",
            description: "Currently helping organizations implement cutting-edge AI solutions and automate complex workflows.",
            tech: ["Generative AI", "LLMs", "Automation", "Consulting"]
        }
    ];

    const values = [
        {
            icon: "💡",
            title: "Innovation",
            description: "Constantly exploring new technologies and methodologies to push the boundaries of what's possible with AI."
        },
        {
            icon: "🎯",
            title: "Impact",
            description: "Focused on creating solutions that make a real difference in people's lives and business operations."
        },
        {
            icon: "🤝",
            title: "Collaboration",
            description: "Believing in the power of teamwork and knowledge sharing to achieve extraordinary results."
        }
    ];

    return (
        <AboutContainer ref={containerRef}>
            {/* Hero Section */}
            <HeroSection>
                <HeroContent>
                    <MainTitle
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        About Me
                    </MainTitle>
                    <Subtitle
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        Applied AI Engineer & Full-Stack Developer
                    </Subtitle>
                    <Introduction
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        I'm passionate about leveraging artificial intelligence to solve complex real-world problems. 
                        With expertise spanning machine learning, full-stack development, and research, I bridge the gap 
                        between cutting-edge AI research and practical applications that drive meaningful impact.
                    </Introduction>
                </HeroContent>
            </HeroSection>

            {/* Stats Section */}
            <StatsSection
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                <StatsGrid>
                    <StatCard
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <StatNumber>15+</StatNumber>
                        <StatLabel>AI/ML Projects</StatLabel>
                    </StatCard>
                    <StatCard
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <StatNumber>3+</StatNumber>
                        <StatLabel>Years Experience</StatLabel>
                    </StatCard>
                    <StatCard
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <StatNumber>5+</StatNumber>
                        <StatLabel>Research Papers</StatLabel>
                    </StatCard>
                    <StatCard
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <StatNumber>10+</StatNumber>
                        <StatLabel>Technologies</StatLabel>
                    </StatCard>
                </StatsGrid>
            </StatsSection>

            {/* Skills Section */}
            <ContentSection>
                <SectionTitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    Core Expertise
                </SectionTitle>
                <SectionSubtitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    A comprehensive skill set spanning AI, development, and research
                </SectionSubtitle>
                
                <SkillsGrid>
                    {skillsData.map((category, index) => (
                        <SkillCategory
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            whileHover={{ scale: 1.02 }}
                        >
                            <CategoryTitle>
                                <span style={{ fontSize: '1.5rem' }}>{category.icon}</span>
                                {category.title}
                            </CategoryTitle>
                            <SkillsList>
                                {category.skills.map((skill, skillIndex) => (
                                    <SkillItem key={skillIndex}>{skill}</SkillItem>
                                ))}
                            </SkillsList>
                        </SkillCategory>
                    ))}
                </SkillsGrid>
            </ContentSection>

            {/* Journey Section */}
            <JourneySection>
                <SectionTitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    My Journey
                </SectionTitle>
                <SectionSubtitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    From curious student to AI engineer - here's how I got here
                </SectionSubtitle>
                
                <JourneyGrid>
                    {journeyData.map((item, index) => (
                        <JourneyItem
                            key={index}
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: index * 0.1 }}
                            viewport={{ once: true }}
                        >
                            <JourneyTitle>{item.title}</JourneyTitle>
                            <JourneyDescription>{item.description}</JourneyDescription>
                            <JourneyTech>
                                {item.tech.map((tech, techIndex) => (
                                    <TechTag key={techIndex}>{tech}</TechTag>
                                ))}
                            </JourneyTech>
                        </JourneyItem>
                    ))}
                </JourneyGrid>
            </JourneySection>

            {/* Values Section */}
            <ValuesSection>
                <SectionTitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    What Drives Me
                </SectionTitle>
                <SectionSubtitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                    style={{ margin: '0 auto 3rem' }}
                >
                    The principles that guide my work and passion for technology
                </SectionSubtitle>
                
                <ValuesGrid>
                    {values.map((value, index) => (
                        <ValueCard
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: index * 0.2 }}
                            viewport={{ once: true }}
                        >
                            <ValueIcon>{value.icon}</ValueIcon>
                            <ValueTitle>{value.title}</ValueTitle>
                            <ValueDescription>{value.description}</ValueDescription>
                        </ValueCard>
                    ))}
                </ValuesGrid>
            </ValuesSection>

            {/* CTA Section */}
            <CTASection>
                <SectionTitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    Let's Work Together
                </SectionTitle>
                <Introduction
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    I'm always excited to discuss new opportunities, collaborate on innovative projects, 
                    or simply chat about the latest developments in AI and technology.
                </Introduction>
                
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                >
                    <CTAButton
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/contact')}
                    >
                        Get In Touch
                    </CTAButton>
                    <SecondaryButton
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => window.open('https://github.com/ukexe', '_blank')}
                    >
                        View GitHub
                    </SecondaryButton>
                </motion.div>
            </CTASection>
        </AboutContainer>
    );
};

export default AboutPage;
